import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import UpgradePlanModal from 'pages/Home/RecurringPage/UpgradePlanModal';

import { AUTOCOMPLETE, ENUMS, SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import AutomationConditionsTable, {
	AutomationConditionsConfig,
	convertAutomationConditionValue
} from '../AutomationConditionsTable/AutomationConditionsTable';
import * as S from './OtomationTestForm.styled';

const defaultColSpan = {
	md: 6
};

const priorityMethodEnums = {
	otoRates: {
		ScalePackage: ENUMS.PRIORITY_METHODS_AGGREGATOR_SCALE,
		EnterprisePackage: ENUMS.PRIORITY_METHODS_AGGREGATOR_ENTERPRISE
	},
	clientRates: {
		ScalePackage: ENUMS.PRIORITY_METHODS_INTEGRATOR_SCALE,
		EnterprisePackage: ENUMS.PRIORITY_METHODS_INTEGRATOR_ENTERPRISE
	}
};

const OtomationRuleForm = NiceModal.create(({ id, mode, modalName }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const modal = useModal();
	const { showSuccess, showError, showInfo } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const isAuthorized = useAccessControl();
	const { closeModal } = useModalRouter();
	const currentPlanName = useSelector((state) => state.user.planName);

	const [isLoading, setIsLoading] = useState(false);
	const [enumName, setEnumName] = useState(ENUMS.PRIORITY_METHODS_V2);

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const validateAtLeastOneValue = (context) => {
		const { valueAsString, valueAsNumber, valueAsBoolean, valueAsTime } = context.parent ?? {};
		return valueAsString || valueAsNumber || valueAsBoolean || valueAsTime;
	};

	const schema = useMemo(
		() =>
			yup.object().shape({
				ruleName: yup.string().nullable().required(),
				ruleId: yup.number().nullable(),
				dataPeriod: yup.number().nullable(),
				partnerType: yup.string().nullable().required(),
				priorityMethods: yup.string().nullable().required(),
				status: yup.string().nullable().required(),
				retryWithNextDc: yup.boolean().nullable(),
				pickupMethod: yup
					.string()
					.nullable()
					.when('partnerType', {
						is: (partnerType) => partnerType !== 'clientRates',
						then: (schema) => schema.required(),
						otherwise: (schema) => schema.nullable()
					}),
				deliveryMethod: yup
					.string()
					.nullable()
					.when('partnerType', {
						is: (partnerType) => partnerType !== 'clientRates',
						then: (schema) => schema.required(),
						otherwise: (schema) => schema.nullable()
					}),
				serviceType: yup.string().nullable(),
				maxPrice: yup.number().nullable(),
				conditions: yup.array().of(
					yup.object().shape({
						condition: yup.string().nullable().required(),
						operator: yup
							.string()
							.nullable()
							.when('condition', ([condition], schema) => {
								const item = AutomationConditionsConfig.find((item) => item.name === condition);
								return item?.type ? schema.required() : schema;
							}),
						valueAsString: yup
							.mixed()
							.nullable()
							.test('at-least-one', t('alert.requiredField'), (_, context) =>
								validateAtLeastOneValue(context)
							)
							.test('is-string-or-array', t('alert.invalidField'), (value) => {
								if (!value) return true;
								return typeof value === 'string' || Array.isArray(value);
							}),
						valueAsNumber: yup
							.number()
							.nullable()
							.test('at-least-one', t('alert.requiredField'), (_, context) =>
								validateAtLeastOneValue(context)
							),
						valueAsBoolean: yup
							.boolean()
							.nullable()
							.test('at-least-one', t('alert.requiredField'), (_, context) =>
								validateAtLeastOneValue(context)
							),
						valueAsTime: yup
							.string()
							.nullable()
							.test('at-least-one', t('alert.requiredField'), (_, context) =>
								validateAtLeastOneValue(context)
							)
					})
				),
				deliveryCompanies: yup
					.array()
					.of(
						yup
							.object()
							.shape({
								dcSettingsId: yup
									.number()
									.nullable()
									.when('priorityMethods', ([priorityMethods], schema) => {
										if (
											priorityMethods === 'manualSelectionIntegrator' ||
											priorityMethods === 'manualSelectionAggregator' ||
											priorityMethods === 'percentageDistribute'
										) {
											return schema.required();
										} else {
											return schema;
										}
									}),
								value: yup.number().nullable()
							})
							.test('requiredField', t('alert.requiredField'), (value, context) => {
								const method = watch('priorityMethods');
								const id = value?.dcSettingsId;

								if (
									!id &&
									(method === 'manualSelectionIntegrator' ||
										method === 'manualSelectionAggregator' ||
										method === 'percentageDistribute')
								) {
									return context.createError({
										path: `${context.path}.dcSettingsId`,
										message: t('alert.requiredField'),
										type: 'manual'
									});
								}
								return true;
							})
							.test('manual', t('errorMsg.duplicateDC'), (value, context) => {
								const id = value.dcSettingsId;
								const ids = context.parent.map((item) => item.dcSettingsId);
								const isValueNotUnique = ids.indexOf(id) !== ids.lastIndexOf(id);

								return isValueNotUnique
									? context.createError({
											path: `${context.path}.dcSettingsId`,
											message: t('errorMsg.duplicateDC'),
											type: 'manual'
										})
									: true;
							})
					)
					.nullable()
			}),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			ruleName: null,
			status: null,
			shipmentType: null,
			conditions: [{ condition: null, operator: null, value: null }],
			retryWithNextDc: false,
			id: null,
			dataPeriod: 1,
			partnerType: null,
			pickupMethod: null,
			deliveryCompanies: null,
			deliveryMethod: null,
			maxPrice: null,
			serviceType: null
		};

		if (!id) {
			return defaultValues;
		}

		setIsLoading(true);
		try {
			const { data, status } = await axiosPrivate.post(SHIPPING_AUTOMATION.GET_AUTOMATION_RULE, { ruleId: id });
			if (status === 200) {
				return {
					...defaultValues,
					ID: data?.ID ?? null,
					ruleName: data.name ?? null,
					ruleId: data.ruleId ?? null,
					dataPeriod: data.dataPeriod ?? 1,
					partnerType: data.partnerType ?? null,
					priorityMethods: data.method ?? null,
					pickupMethod: data.pickupMethod ?? null,
					deliveryMethod: data.deliveryMethod ?? null,
					serviceType: data.serviceType ?? null,
					maxPrice: data.maxPrice ?? null,
					retryWithNextDc: data.retryWithNextDc ?? false,
					status: data.status ?? null,
					deliveryCompanies:
						data?.deliveryCompanies?.map((deliveryCompany) => ({
							dcSettingsId: deliveryCompany.dcSettingsId,
							dcName: deliveryCompany.dcName,
							distributionValue: deliveryCompany.distributionValue,
							rank: deliveryCompany.rank ?? 1,
							dcIntegrationName: deliveryCompany.dcIntegrationName ?? null
						})) ?? undefined,
					conditions: data.conditions?.map((item) => {
						const parameter = AutomationConditionsConfig.find(
							(condition) => condition.name === item.conditionType
						);
						return {
							...item,
							...convertAutomationConditionValue(item, parameter),
							condition:
								AutomationConditionsConfig.find(
									(condition) =>
										condition?.nameV1 === item.conditionType ||
										condition?.name === item.conditionType
								)?.name ?? item.condition,
							operator: item.conditionOperator ?? null,
							ID: item.ID ?? null
						};
					})
				};
			} else {
				throw new Error();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		} finally {
			setIsLoading(false);
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		control,
		setError,
		clearErrors,
		errors,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const partnerType = useWatch({ control, name: 'partnerType' });
	useEffect(() => {
		const selectedEnum = priorityMethodEnums[partnerType]?.[currentPlanName];
		if (selectedEnum) {
			setEnumName(selectedEnum);
		}
	}, [currentPlanName, partnerType]);

	const onSubmit = (data) => {
		setIsLoading(true);

		const rankedDeliveryCompanies = data.deliveryCompanies?.map((item, index) => ({
			...item,
			rank: index + 1
		}));

		axiosPrivate
			.post(SHIPPING_AUTOMATION.SAVE_AUTOMATION_RULE, {
				id,
				name: data.ruleName,
				dataPeriod: ['slaMet', 'customerRating', 'deliveryTime', 'deliveryRate'].includes(priorityMethods)
					? data.dataPeriod
					: undefined,
				partnerType: data.partnerType,
				method: data.priorityMethods,
				status: data.status,
				pickupMethod: data.pickupMethod,
				deliveryMethod: data.deliveryMethod,
				serviceType: data.serviceType,
				maxPrice: data.maxPrice,
				deliveryCompanies: rankedDeliveryCompanies,
				retryWithNextDc: data?.retryWithNextDc,
				conditions: data.conditions?.map((item) => ({
					id: item.id ? item.id : null,
					conditionType: item.condition,
					conditionOperator: item.operator === 'equalCustom' ? 'equals' : item.operator,
					valueAsBoolean: item?.valueAsBoolean ?? undefined,
					valueAsNumber: item?.valueAsNumber ?? undefined,
					valueAsString:
						(item?.condition === 'shippingDescription'
							? item?.valueAsString.map((item) => item).join(',')
							: item?.valueAsString) ?? undefined,
					valueAsTime: item?.valueAsTime ?? undefined
				}))
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.OTOMATION_RULES));
					handleCloseModal();
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				if (error.response.data.code === 'error.SA002' || error.response.data.code === 'error.SA006') {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message,
						header: error.response.data.message
					});
					setError('ruleId', {
						type: 'manual',
						message: error?.response?.data?.code
					});
				} else {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const priorityMethods = watch('priorityMethods');

	return (
		<Modal fullwidth maxWidth="large" name={modalName} className="modal-modal-otomation-rule-form">
			<ModalTitle>
				<Typography variant="p1">
					{isViewMode ? 'viewOtomationRule' : id ? 'editOtomationRule' : 'addOtomationRule'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="ruleName" isLoading={isLoading} required>
						<StringInput name="ruleName" disabled={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading} required>
						<EnumSelect
							disabled={isViewMode}
							name="status"
							placeholder="selectOne"
							enumName={ENUMS.FEASIBILITY_STATUS}
							setValue={(value) => {
								setValue('status', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('status')}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="partnerType" isLoading={isLoading} required>
						<EnumSelect
							disabled={isViewMode}
							name="partnerType"
							placeholder="selectOne"
							enumName={ENUMS.OTOMATION_RULES_PARTNER_TYPE}
							setValue={(value) => {
								setValue('partnerType', value, { shouldValidate: true, shouldDirty: true });
								setValue('priorityMethods', null, { shouldValidate: true, shouldDirty: true });
							}}
							value={partnerType}
						/>
					</FormItem>

					{['slaMet', 'customerRating', 'deliveryTime', 'deliveryRate'].includes(priorityMethods) && (
						<FormItem colSpan={defaultColSpan} label="dataPeriod" isLoading={isLoading}>
							<IntegerInput showArrows={false} unit="months" name="dataPeriod" disabled={isViewMode} />
						</FormItem>
					)}

					<FormItem label="conditions" required>
						<AutomationConditionsTable
							viewMode={isViewMode}
							isLoading={isLoading}
							conditionsEnumName={
								isAuthorized('EnterprisePackage')
									? ENUMS.FEASIBILITY_CONDITIONS_ENTERPRISE_V2
									: ENUMS.FEASIBILITY_CONDITIONS_SCALE_V2
							}
						/>
					</FormItem>

					{partnerType && partnerType === 'otoRates' && (
						<>
							<S.Line></S.Line>
							<Typography gutterBottom component="h1" variant="p2" fullWidth fontWeight="700">
								deliveryCompanyFilters
							</Typography>
							<FormItem colSpan={defaultColSpan} label="pickupMethod" isLoading={isLoading} required>
								<EnumSelect
									disabled={isViewMode}
									name="pickupMethod"
									placeholder="selectOne"
									enumName={ENUMS.SHIPPING_OTOMATION_PICKUP_METHODS}
									setValue={(value) => {
										setValue('pickupMethod', value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									value={watch('pickupMethod')}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="deliveryMethod" isLoading={isLoading} required>
								<EnumSelect
									disabled={isViewMode}
									name="deliveryMethod"
									placeholder="selectOne"
									enumName={ENUMS.SHIPPING_OTOMATION_DELIVERY_METHODS}
									setValue={(value) => {
										setValue('deliveryMethod', value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									value={watch('deliveryMethod')}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="serviceType" isLoading={isLoading}>
								<EnumSelect
									disabled={isViewMode}
									name="serviceType"
									placeholder="selectOne"
									enumName={ENUMS.SHIPPING_OTOMATION_SERVICE_TYPES}
									setValue={(value) => {
										setValue('serviceType', value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									value={watch('serviceType')}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="maxPrice" isLoading={isLoading}>
								<CurrencyInput
									disabled={isViewMode}
									name="maxPrice"
									setValue={(value) => {
										setValue(`maxPrice`, value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
								/>
							</FormItem>
						</>
					)}

					<>
						<S.Line></S.Line>
						<FormItem
							colSpan={defaultColSpan}
							label="method"
							isLoading={isLoading}
							required
							customLabelContent={
								currentPlanName === 'EnterprisePackage' ? null : (
									<Tooltip
										timeout={100}
										placement="right"
										color={'var(--white)'}
										title={
											<div>
												<ModuleToolTipContainer
													lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/border/upgradeRocket.json.gz"
													title="shippingAutomationModuleConditionTitle"
													description="shippingAutomationModuleConditionSubtitle"
												/>

												<Button
													className="mt-3 mx-auto"
													label="upgradePlan"
													variant="blue"
													onClick={() =>
														NiceModal.show(UpgradePlanModal, {
															enumPropFeasibility: false,
															enumPropPriority: true,
															imageUrl:
																'https://storage.googleapis.com/tryoto-public/svg/upgradePlan-illustration.svg',
															title: 'upgradeFeasibilityTitle',
															subtitle: 'upgradeFeasibilitySubtitle'
														})
													}
												/>
											</div>
										}>
										<Icon icon={faCircleInfo} color="var(--manageModuleInfoColor)" />
									</Tooltip>
								)
							}>
							<EnumSelect
								disabled={isViewMode}
								name="priorityMethods"
								placeholder="selectOne"
								enumName={enumName}
								setValue={(value) => {
									setValue('priorityMethods', value ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
									setValue(
										'deliveryCompanies',
										value === 'manualSelectionIntegrator' || value === 'manualSelectionAggregator'
											? [
													{
														dcSettingsId: null,
														rank: 1,
														dcName: null,
														distributionValue: null,
														dcIntegrationName: null
													}
												]
											: []
									);
									clearErrors('deliveryCompanies');
								}}
								value={priorityMethods}
							/>
						</FormItem>

						<FormItem
							colSpan={defaultColSpan}
							label="retryWithNextDc"
							isLoading={isLoading}
							direction="row">
							<SwitchInput
								value={watch('retryWithNextDc')}
								setValue={(e) => {
									setValue('retryWithNextDc', e.target.checked, {
										shouldValidate: true,
										shouldDirty: true
									});
									if (e.target.checked) {
										showInfo({
											header: 'retryWithNextDc.notification.header',
											message: 'retryWithNextDc.notification.content'
										});
									}
								}}
								disabled={isViewMode}
							/>
						</FormItem>
					</>

					{(priorityMethods === 'manualSelectionIntegrator' ||
						priorityMethods === 'manualSelectionAggregator' ||
						priorityMethods === 'percentageDistribute') && (
						<FormItem label="actions" required className="table">
							<ReturnTable viewMode={isViewMode} isLoading={isLoading} partnerType={partnerType} />
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty || errors?.deliveryCompanies}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

const ReturnTable = ({ isLoading, viewMode, partnerType }) => {
	const { control, setValue, watch, trigger } = useFormContext();
	const { fields, append, remove, move } = useFieldArray({
		control,
		name: 'deliveryCompanies'
	});
	const value = watch('priorityMethods');

	const deliveryCompanyColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const isMobile = useMediaQuery(Breakpoints.mobile);

	const columnsPercentageDistribute = useMemo(
		() => [
			{
				id: 'deliveryCompany',
				header: 'deliveryCompany',
				accessor: 'deliveryCompany',
				size: isMobile ? 40 : 120,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						disabled={viewMode}
						name={`deliveryCompanies[${index}].dcSettingsId`}
						value={watch(`deliveryCompanies[${index}].dcSettingsId`)}
						placeholder="search"
						columns={deliveryCompanyColumns}
						selectionLabelAccessor={['Name']}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;

							setValue(`deliveryCompanies[${index}].dcSettingsId`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});

							trigger('deliveryCompanies');
						}}
						autoCompleteName={
							partnerType === 'otoRates'
								? AUTOCOMPLETE.DELIVERY_COMPANY_ACTIVE_AGGREGATOR
								: AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS
						}
					/>
				)
			},
			{
				header: 'distributionValue',
				accessor: 'distributionValue',
				size: isMobile ? 20 : 40,
				renderCell: ({ row: { index } }) => (
					<IntegerInput
						disabled={viewMode}
						showArrows={false}
						required
						unit="%"
						name={`deliveryCompanies[${index}].distributionValue`}
						value={watch(`deliveryCompanies[${index}].distributionValue`)}
					/>
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[isMobile, value]
	);
	const columnsManualRating = useMemo(
		() => [
			{
				id: 'rank',
				size: 10,
				maxSize: 10,
				header: 'rank',
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<div style={{ display: 'flex' }}>
						<Typography>{index + 1}</Typography>
					</div>
				)
			},
			{
				id: 'deliveryCompanySettingsID',
				header: 'deliveryCompanySetting',
				accessor: 'deliveryCompanySettingsID',
				size: isMobile ? 40 : 120,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						disabled={viewMode}
						name={`deliveryCompanies[${index}].dcSettingsId`}
						value={watch(`deliveryCompanies[${index}].dcSettingsId`)}
						placeholder="search"
						columns={deliveryCompanyColumns}
						selectionLabelAccessor={['Name']}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;
							setValue(`deliveryCompanies[${index}].dcSettingsId`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`deliveryCompanies[${index}].dcIntegrationName`, value?.integrationName ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`deliveryCompanies[${index}].dcName`, value?.Name ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`deliveryCompanies[${index}].distributionValue`, null, {
								shouldValidate: true,
								shouldDirty: true
							});

							trigger('deliveryCompanies');
						}}
						autoCompleteName={
							partnerType === 'otoRates'
								? AUTOCOMPLETE.DELIVERY_COMPANY_ACTIVE_AGGREGATOR
								: AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS
						}
					/>
				)
			},
			...(!viewMode
				? [
						{
							id: 'deleteRow',
							size: 20,
							maxSize: 20,
							showOnMobile: true
						}
					]
				: [])
		],
		[isMobile, value]
	);
	const columnsRest = useMemo(
		() => [
			{
				id: 'deliveryCompany',
				accessor: 'deliveryCompany',
				header: 'deliveryCompany',
				size: isMobile ? 40 : 120,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						disabled={viewMode}
						name={`deliveryCompanies[${index}].dcSettingsId`}
						value={watch(`deliveryCompanies[${index}].dcSettingsId`)}
						placeholder="search"
						columns={deliveryCompanyColumns}
						selectionLabelAccessor={['Name']}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;

							setValue(`deliveryCompanies[${index}].dcSettingsId`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});

							trigger('deliveryCompanies');
						}}
						autoCompleteName={
							partnerType === 'otoRates'
								? AUTOCOMPLETE.DELIVERY_COMPANY_ACTIVE_AGGREGATOR
								: AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS
						}
					/>
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[isMobile, value]
	);

	if (value === 'percentageDistribute') {
		return (
			<Table
				key={'table'}
				loading={isLoading}
				data={fields}
				columns={columnsPercentageDistribute}
				onRowDelete={(row) => {
					remove(row.index);
				}}
				onRowAdd={() => {
					append({
						dcSettingsId: null,
						distributionValue: null,
						dcName: null,
						rank: watch('deliveryCompanies').length,
						dcIntegrationName: null
					});
				}}
				showAddNewRowButton={!viewMode}
			/>
		);
	}
	if (value === 'manualSelectionIntegrator' || value === 'manualSelectionAggregator') {
		return (
			<Table
				key={'table'}
				loading={isLoading}
				data={fields}
				columns={columnsManualRating}
				onRowDelete={(row) => {
					remove(row.index);
				}}
				onRowAdd={() => {
					append({
						dcSettingsId: null,
						distributionValue: null,
						dcName: null,
						rank: watch('deliveryCompanies').length + 1,
						dcIntegrationName: null
					});
				}}
				showAddNewRowButton={!viewMode}
				onRowDrag={move}
			/>
		);
	} else {
		return (
			<Table
				key={'table'}
				loading={isLoading}
				data={fields}
				columns={columnsRest}
				onRowDelete={(row) => {
					remove(row.index);
				}}
				onRowAdd={() => {
					append({
						dcSettingsId: null,
						distributionValue: null,
						dcName: null,
						rank: watch('deliveryCompanies').length + 1,
						dcIntegrationName: null
					});
				}}
				showAddNewRowButton={!viewMode}
			/>
		);
	}
};

export default OtomationRuleForm;
